import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Copy, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, } from '@/components/ui/dialog';
import { td } from '@/lib/i18n';
import { fetchWorkspaceBotInfo } from '@/services/workspace';
/**
 * Compact help affordance: question-mark icon → modal with step-by-step
 * instructions for getting a Telegram chat ID via the Adora bot.
 *
 * Reusable next to any Telegram-chat-ID input — workspace defaults,
 * campaign overrides, internal driver-notifier settings.
 */
export function TelegramChatIdHelp({ workspaceId, label, className, }) {
    const { t } = useTranslation();
    const trigger = label ?? td(t, 'telegram_chat_id_help.trigger', { defaultValue: 'How to get the chat ID' });
    const [open, setOpen] = React.useState(false);
    // Lazy-load bot info: only fire when the modal is opened to avoid
    // hammering Telegram's getMe on every page render.
    const { data: botInfo } = useQuery({
        queryKey: ['workspace-bot-info', workspaceId],
        queryFn: () => fetchWorkspaceBotInfo(workspaceId),
        enabled: open && Boolean(workspaceId),
        staleTime: 5 * 60_000,
    });
    const botUsername = botInfo?.username ?? null;
    const botHandle = botUsername ? `@${botUsername}` : null;
    return (<Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button type="button" aria-label={trigger} title={trigger} className={'inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground transition-colors ' +
            (className ?? '')}>
                    <HelpCircle className="h-4 w-4"/>
                </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>
                        {td(t, 'telegram_chat_id_help.title', { defaultValue: 'How to get a Telegram chat ID' })}
                    </DialogTitle>
                    <DialogDescription>
                        {td(t, 'telegram_chat_id_help.subtitle', {
            defaultValue: 'The Adora bot will reply with the chat ID when you message it. Takes about 30 seconds.',
        })}
                    </DialogDescription>
                </DialogHeader>

                <ol className="space-y-4 pt-2">
                    <Step n={1} title={td(t, 'telegram_chat_id_help.step_1_title', {
            defaultValue: 'Add the Adora bot to your chat',
        })} body={<>
                                <p>
                                    {td(t, 'telegram_chat_id_help.step_1_body', {
                defaultValue: 'Open the group / channel in Telegram → ⓘ → Add member → search for the bot below. For a 1:1 chat, just open a conversation with it.',
            })}
                                </p>
                                {botHandle && (<div className="mt-2">
                                        <CopyChip value={botHandle}/>
                                    </div>)}
                                {botInfo && !botUsername && (<p className="mt-2 text-xs text-amber-500">
                                        {td(t, 'telegram_chat_id_help.bot_unknown', {
                    defaultValue: 'Bot token isn’t configured yet — ask an internal admin to set it in /console/internal/settings.',
                })}
                                    </p>)}
                            </>}/>
                    <Step n={2} title={td(t, 'telegram_chat_id_help.step_2_title', {
            defaultValue: 'Send /chatid in the chat',
        })} body={<>
                                <p>
                                    {td(t, 'telegram_chat_id_help.step_2_body', {
                defaultValue: 'Type one of the commands below and send it. The bot will reply with the chat ID.',
            })}
                                </p>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {['/chatid', '/start', '/whoami'].map((cmd) => (<CopyChip key={cmd} value={cmd}/>))}
                                </div>
                            </>}/>
                    <Step n={3} title={td(t, 'telegram_chat_id_help.step_3_title', {
            defaultValue: 'Copy the chat ID into the input',
        })} body={td(t, 'telegram_chat_id_help.step_3_body', {
            defaultValue: 'Group chats look like -1001234567890. Direct chats are positive numbers like 12345678. Paste it back here and save.',
        })}/>
                </ol>

                <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                    <CheckCircle2 className="inline h-3.5 w-3.5 mr-1 text-emerald-500"/>
                    {td(t, 'telegram_chat_id_help.note', {
            defaultValue: 'The bot must already be added to the chat to reply. If nothing happens, check that the bot is a member and the workspace’s bot token is configured.',
        })}
                </div>
            </DialogContent>
        </Dialog>);
}
function Step({ n, title, body, }) {
    return (<li className="flex gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                {n}
            </span>
            <div className="space-y-0.5 text-sm">
                <p className="font-medium">{title}</p>
                <div className="text-muted-foreground leading-relaxed">{body}</div>
            </div>
        </li>);
}
function CopyChip({ value }) {
    const onCopy = () => {
        navigator.clipboard
            .writeText(value)
            .then(() => toast.success(`Copied "${value}"`))
            .catch(() => toast.error('Copy failed'));
    };
    return (<button type="button" onClick={onCopy} className="inline-flex items-center gap-1.5 rounded-md border bg-background px-2 py-1 font-mono text-xs hover:bg-muted transition-colors">
            {value}
            <Copy className="h-3 w-3 text-muted-foreground"/>
        </button>);
}
